import React, { useState, Fragment, useEffect, useCallback } from 'react';

import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import UpwardBadge from './UpwardBadge';
import { ORGANIZATION_TYPE_OPTIONS } from 'app/employer/pages/accountStatus/form-elements/SelectOrganizationType';
import { CANDIDATE_RATING_OPTIONS } from 'app/employer/pages/candidate/form-elements/SelectCandidateRating';
import { candidateResumeStatusOptions } from 'app/employer/pages/candidate/form-elements/CandidateResumeStatus';
import { FilterIcon } from 'app/employer/vectors';
import { getLabelDateRange } from '../redux-form/fields/ReduxSelectDateRange';

const getBadgesArray = (filter) => {
   const getBadgeItem = (title, value, identifier) => {
      let filtered;
      switch (identifier) {
         case 'organizationName':
         case 'organizationType':
            filtered = ORGANIZATION_TYPE_OPTIONS.filter((item) => item.value === value);
            break;
         case 'rating':
            filtered = CANDIDATE_RATING_OPTIONS.filter((item) => item.value === value);
            break;
         case 'resumeStatus':
            filtered = candidateResumeStatusOptions.filter((item) => item.value === value);
            break;

         default:
            break;
      }
      if (filtered && filtered[0]) {
         value = filtered[0].label;
      }
      return {
         text: <><b style={ { fontSize: '0.85em' } }>{ title }</b>: <small>{ value }</small></>,
         identifier: identifier,
      };
   };

   const {
      fromDate,
      toDate,
      scopes,
      advanced,
      searchText,
      categoryName,
      organization,
      rule,
      hiringCompany,
      jobGroup,
      source,
      publisher,
      contract,
      device,
      ruleType,
      advertiser,
      company,
      name,
      radius,
      active,
      location,
      jobTitle,
   } = filter ? filter.getReduxFormValues() : {};

   let badgeArray = [];
   if (fromDate && toDate) {
      // badgeArray.push(getBadgeItem('Date', <Fragment><DateFormatter timeStamp={fromDate.getTime()} /> - <DateFormatter
      // timeStamp={toDate.getTime()} /></Fragment>, 'date'));
   }
   if (searchText) {
      badgeArray.push(getBadgeItem('Job Title', searchText, 'searchText'));
   }

   if (categoryName) {
      badgeArray.push(getBadgeItem('Category', categoryName, 'categoryName'));
   }

   if (name) {
      badgeArray.push(getBadgeItem('Name', name, 'name'));
   }

   if (jobGroup) {
      if (jobGroup.name && jobGroup.id) {
         badgeArray.push(getBadgeItem('Job Group', jobGroup.name, 'jobGroup'));
      } else {
         badgeArray.push(getBadgeItem('Job Group', jobGroup, 'jobGroup'));
      }
   }

   if (organization) {
      if (organization.name && organization.id) {
         badgeArray.push(getBadgeItem('Organization', organization.name, 'organizationName'));
      } else {
         badgeArray.push(getBadgeItem('Organization', organization, 'organization'));
      }
   }

   if (company) {
      if (company.name && company.id) {
         badgeArray.push(getBadgeItem('Company', company.name, 'companyName'));
      } else {
         badgeArray.push(getBadgeItem('Company', company, 'company'));
      }
   }

   if (rule) {
      badgeArray.push(getBadgeItem('Rule', rule, 'rule'));
   }

   if (hiringCompany) {
      badgeArray.push(getBadgeItem('Hiring Company', hiringCompany, 'hiringCompany'));
   }

   if (source) {
      badgeArray.push(getBadgeItem('Source', source, 'source'));
   }

   if (publisher) {
      badgeArray.push(getBadgeItem('Publisher', publisher, 'publisher'));
   }

   if (contract) {
      badgeArray.push(getBadgeItem('Contract', contract, 'contract'));
   }

   if (device) {
      badgeArray.push(getBadgeItem('Device', device, 'device'));
   }

   if (active) {
      if (active === 'all') {
         badgeArray.push(getBadgeItem('Job Title Status', 'All Jobs', 'active'));
      } else if (active === 'active') {
         badgeArray.push(getBadgeItem('Job Title Status', 'Active', 'active'));
      } else if (active === 'inactive') {
         badgeArray.push(getBadgeItem('Job Title Status', 'Inactive', 'active'));
      }
   }

   if (location) {
      badgeArray.push(getBadgeItem('Location', location, 'location'));
   }

   if (jobTitle) {
      badgeArray.push(getBadgeItem('Job Title', jobTitle, 'jobTitle'));
   }

   if (ruleType) {
      if (ruleType === 'traffic_mix') {
         badgeArray.push(getBadgeItem('Rule Type', 'Traffic Mix', 'ruleType'));
      } else if (ruleType === 'advertiser_optimization') {
         badgeArray.push(getBadgeItem('Rule Type', 'Optimization', 'ruleType'));
      } else if (ruleType === 'publisher_blacklist') {
         badgeArray.push(getBadgeItem('Rule Type', 'Blacklist', 'ruleType'));
      } else if (ruleType === 'publisher_cpc_floor') {
         badgeArray.push(getBadgeItem('Rule Type', 'CPC Floor', 'ruleType'));
      } else if (ruleType === 'publisher_company_blacklist') {
         badgeArray.push(getBadgeItem('Rule Type', 'Company Blacklist', 'ruleType'));
      } else if (ruleType === 'publisher_taxonomy_blacklist') {
         badgeArray.push(getBadgeItem('Rule Type', 'Taxonomy Blacklist', 'ruleType'));
      } else if (ruleType === 'publisher_experience') {
         badgeArray.push(getBadgeItem('Rule Type', 'User Experience', 'ruleType'));
      } else if (ruleType === 'expired_jobs') {
         badgeArray.push(getBadgeItem('Rule Type', 'Expired Jobs', 'ruleType'));
      } else if (ruleType === 'publisher_sand_box') {
         badgeArray.push(getBadgeItem('Rule Type', 'Sandbox', 'ruleType'));
      }
   }

   if (advertiser && advertiser.name && advertiser.id) {
      badgeArray.push(getBadgeItem('Advertiser', advertiser.name, 'advertiserName'));
   }

   if (radius) {
      badgeArray.push(getBadgeItem('Radius', radius.value, 'radius'));
   }

   if (scopes) {
      // get the scopes with repeatable values (jobGroupIds, organizationIds, hiringCompanyIds)
      const nonRepeatableScopes = scopes.filter(({ type }) => !(type && type.repeatable));
      const repeatableScopes = scopes.filter(({ type }) => (type && type.repeatable));

      nonRepeatableScopes.forEach(item => {
         if (Object.entries(item).length !== 0) {
            if (item.type.value === 'feedType' && (item.value === 'feed_log_employer' || item.value === 'feed_log_sj')) {
               badgeArray.push(getBadgeItem(item.type.label, item.value === 'feed_log_employer' ? 'DE' : 'JF', item.type.value));
            } else {
               let value;

               if (item.value.text) {
                  value = item.value.text;
               } else if (item.value.name) {
                  value = item.value.name;
               } else {
                  value = item.value;
               }

               badgeArray.push(getBadgeItem(item.type.label, value, item.type.value));
            }
         }
      });

      // Reducer for repeatable values : groups multi-selected ids. i.e "Organization : Jobtome, Upward.net, etc. "
      const reducer = (accumulator, scope) => {
         const KEY = scope.type.label;
         const VALUE = scope.value.name ? scope.value.name : scope.value;

         const valueIsString = (typeof VALUE) === 'string';

         if (valueIsString) {
            if (accumulator[KEY]) {
               accumulator[KEY].push(VALUE);
            } else {
               accumulator[KEY] = [VALUE];
            }
         }

         return accumulator;
      };

      if (repeatableScopes.length > 0) {
         const groupedScopes = repeatableScopes.reduce(reducer, {});
         Object.entries(groupedScopes)
            .forEach(([label, value]) => {
               badgeArray.push(getBadgeItem(label, value.join(', ')));
            });
      }
   }
   if (advanced) {
      const len = advanced.length;
      let item;

      for (let i = 0; i < len; i++) {
         item = advanced[i];
         if (item && item.type && item.type.label && item.value) {
            let value;
            if (typeof (item.value) === 'object') {
               if (item.value.name) {
                  value = item.value.name;
               } else if (item.value.from && item.value.to) {
                  value = getLabelDateRange(item.value.from, item.value.to);
               } else if (item.value.from === null && item.value.to === null) {
                  value = 'None';
               }
            } else {
               value = item.value;
            }

            badgeArray.push(getBadgeItem(item.type.label, value, item.type.value));
         }
      }
   }

   return badgeArray;
};

const FilterBadgeDisplay = ({
   filter,
   onClick,
   closeAble,
   setMainFilterModel,
}) => {
   const [badges, setBadges] = useState([]);
   const [localFilter, setLocalFilter] = useState(null);
   let history = useHistory();
   let location = useLocation();

   useEffect(() => {
      if (filter) {
         setLocalFilter(filter);
         setBadges([...getBadgesArray(filter)]);
      }
   }, [filter]);

   const handleBadgeClose = useCallback((item) => {
      // @TODO: badge close bulk scope
      const identifier = item && item.identifier ? item.identifier : '';

      switch (identifier) {
         case 'location':
            localFilter.setLocation({});
         case 'jobTitle':
            localFilter.setJobTitle({});
         case 'company':
            if (localFilter.setCompany) {
               localFilter.setCompany({});
            } else {
               localFilter.company = undefined;
            }
            break;
         case 'jobGroup':
            if (localFilter.setJobGroup) {
               localFilter.setJobGroup({});
            } else {
               localFilter.jobGroup = undefined;
            }
            break;
         case 'job':
            localFilter.setJob({});
            break;
         case 'hiringCompany':
            if (localFilter.setHiringCompany) {
               localFilter.setHiringCompany({});
            } else {
               localFilter.hiringCompany = undefined;
            }

            break;
         case 'rule':
            localFilter.setRule({});
            break;
         case 'organization':

            if (localFilter.setOrganization) {
               localFilter.setOrganization({});
            } else {
               localFilter.organization = undefined;
            }

            break;
         case 'contract':
            localFilter?.setContract({});
            break;
         case 'publisher':
            localFilter?.setPublisher({});
            break;
         case 'source':
            localFilter.setSource({});
            break;
         case 'contentRule.name':
            if (localFilter?.contentRule) {
               localFilter.contentRule = undefined;
               break;
            }
         case 'rule.name':
            if (localFilter?.rule) {
               localFilter.rule = undefined;
            }
            break;
         case 'organizationType':
         case 'spendStatus':
         case 'candidateName':
         case 'rating':
         case 'resumeStatus':
         case 'ruleType':
         case 'device':
            localFilter[identifier] = undefined;
            break;
         default:
            localFilter[identifier] = undefined;
      }
      setBadges([]);
      if (setMainFilterModel) {
         setMainFilterModel(localFilter);
      }
      history.push(`${ location.pathname }?${ localFilter.getQueryString() }`);
   }, [localFilter]);

   return (
      <>
         { badges.length > 0 && <div className='d-inline mr-2'><FilterIcon width={ 12 } fill='#999999'/></div> }
         { badges.map((item, index) => (
            <UpwardBadge
               key={ index }
               content={ item.text }
               closeAble={ closeAble && item.identifier !== 'date' && item.identifier !== 'display' }
               onClick={ onClick }
               onClose={ () => { handleBadgeClose(item); } }
            />
         )) }
      </>
   );
};

FilterBadgeDisplay.propTypes = {
   closeAble: PropTypes.bool,
   filter: PropTypes.any,
   onClick: PropTypes.func.isRequired,

};

FilterBadgeDisplay.defaultProps = {
   closeAble: true,
};

export default FilterBadgeDisplay;
